// src/polaris-components/RequireProfile.tsx
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { isAthlete, isGuardian, getTermsAccepted, getHasProfile } from '../utils/auth';
import { buildApiUrl } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { isAdminStaffProfileSetupComplete } from '../utils/adminProfileSetup';
import { apiClient } from "../utils/apiClient";

/**
 * RequireProfile - Guards routes that require a complete user profile
 *
 * Checks if the current user has a profile:
 * - For athletes: checks /api/athletes/me
 * - For admin/staff: checks /api/admin-profile (userId extracted from JWT)
 *
 * If no profile exists, redirects to the appropriate profile creation page.
 *
 * This prevents users from accessing any other screens until they complete their profile.
 */
export default function RequireProfile() {
    const location = useLocation();
    const parentContext = useOutletContext();
    const { isAuthenticated } = useAuth();
    const [hasProfile, setHasProfile] = useState<boolean | null>(null);
    const [isChecking, setIsChecking] = useState(true);

    // Pages that should not be protected by profile check
    const exemptPaths = [
        '/add-admin-profile',
        '/athlete/profile',
        '/login',
        '/signup',
        '/checkout',
        '/auth/set-password',
        '/login/callback',
        '/onboarding/welcome',
        '/subscription/success',
        '/subscription/cancel',
        '/guardian/select',
        '/guardian/no-athletes'
    ];

    // Check if current path is exempt
    const isExemptPath = exemptPaths.some(path => location.pathname.startsWith(path));

    useEffect(() => {
        // Skip check for exempt paths
        if (isExemptPath) {
            setHasProfile(true);
            setIsChecking(false);
            return;
        }

        const checkProfile = async () => {
            try {
                // Check authentication via AuthContext
                if (!isAuthenticated) {
                    setHasProfile(true);
                    setIsChecking(false);
                    return;
                }

                // Check terms acceptance first - if not accepted, allow through
                // (TermsAndConditionsModal will handle this)
                const termsAccepted = getTermsAccepted();
                if (!termsAccepted) {
                    setHasProfile(true);
                    setIsChecking(false);
                    return;
                }

                const headers: HeadersInit = {
                    'Accept': 'application/json',
                };

                let apiUrl: string;

                if (isAthlete()) {
                    // Check athlete profile
                    apiUrl = buildApiUrl('api/athletes/me');
                } else if (isGuardian()) {
                    // Guardians don't have their own profile - they view athlete profiles
                    // Skip profile check and allow through
                    setHasProfile(true);
                    setIsChecking(false);
                    return;
                } else {
                    // Check admin/staff profile - userId extracted from JWT
                    apiUrl = buildApiUrl('api/admin-profile');
                }

                const response = await apiClient(apiUrl, {
                    headers,
                    credentials: 'include' // Send HTTP-only cookies
                });

                if (response.ok) {
                    const data = await response.json();

                    // Profile exists if we get a 200 with data
                    let profileExists = !!(data && (data.id || data.firstName || data.lastName));

                    // Admin/staff must finish first-time setup (title, phone, company/school).
                    if (!isAthlete() && !isGuardian() && profileExists) {
                        profileExists = isAdminStaffProfileSetupComplete({
                            title: data.title,
                            telephone: data.telephone,
                            companyName: data.companyName,
                            cleverSchoolName: data.cleverSchoolName,
                            email: data.email,
                        });
                    }

                    setHasProfile(profileExists);
                } else if (response.status === 404) {
                    // 404 means no profile exists
                    setHasProfile(false);
                } else if (response.status === 403) {
                    // 403 might mean terms not accepted - allow through
                    setHasProfile(true);
                } else {
                    // Other errors - log but allow through to avoid blocking user
                    setHasProfile(true);
                }
            } catch (error) {
                // On error, allow through to avoid blocking user
                setHasProfile(true);
            } finally {
                setIsChecking(false);
            }
        };

        checkProfile();
    }, [location.pathname, isExemptPath, isAuthenticated]);

    // Show loading state while checking
    if (isChecking) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '16px',
                color: '#666'
            }}>
                Loading...
            </div>
        );
    }

    // If no profile, redirect to appropriate profile creation page
    if (hasProfile === false) {
        if (isAthlete()) {
            return <Navigate to="/athlete/profile" replace state={{ createProfile: true }} />;
        } else if (isGuardian()) {
            return <Navigate to="/guardian/select" replace />;
        } else {
            return <Navigate to="/add-admin-profile" replace state={{ createProfile: true }} />;
        }
    }

    // Profile exists or exempt path - allow through
    return <Outlet context={parentContext} />;
}

